<script setup lang="ts">
  import { computed, ref } from "vue";
  import { useFormValues, useSetFieldValue } from "vee-validate";
  import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import type { JcdCampaign, JcdBenchmark } from "@/types";

  const props = defineProps<{
    campaign: Partial<JcdCampaign>;
    benchmarks: JcdBenchmark[];
    showResults: boolean;
  }>();

  const values = useFormValues();

  const overrides = [
    { name: "reco_benchmark", label: "Reco (%)", key: "reco" },
    { name: "attribution_benchmark", label: "Attribution (%)", key: "attribution" },
    { name: "intention_benchmark", label: "Intention (%)", key: "intention" },
  ] as const;

  const overrideSetters = Object.fromEntries(overrides.map(({ name }) => [name, useSetFieldValue(name)]));
  const setBenchmarkId = useSetFieldValue("jcd_benchmark_id");

  const isManual = ref(overrides.some(({ name }) => values.value[name] != null));

  const toggleManual = (enabled: boolean) => {
    isManual.value = enabled;
    if (enabled) setBenchmarkId(null);
    else overrides.forEach(({ name }) => overrideSetters[name](null));
  };

  const benchmarkOptions = computed(() =>
    props.benchmarks
      .map((benchmark) => ({ label: benchmark.name, value: benchmark.id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  );

  const selectedBenchmark = computed(
    () => props.benchmarks.find((benchmark) => benchmark.id === values.value.jcd_benchmark_id) || null,
  );

  const formatPct = (value: number | null | undefined) => (value == null ? "—" : `${Math.round(value * 100)}%`);

  const placeholderFor = (key: "reco" | "attribution" | "intention") => {
    const value = selectedBenchmark.value?.[key];
    return value == null ? "—" : String(Math.round(value * 100));
  };

  const benchmarkValue = (key: "reco" | "attribution" | "intention") => {
    const override = values.value[`${key}_benchmark`] as number | null | undefined;
    if (override != null) return `${Math.round(override)}%`;
    return formatPct(selectedBenchmark.value?.[key]);
  };

  const rows = computed(() => [
    { label: "Reco", campaign: formatPct(props.campaign?.reco), benchmark: benchmarkValue("reco") },
    { label: "Attribution", campaign: formatPct(props.campaign?.attribution), benchmark: benchmarkValue("attribution") },
    { label: "Confusion", campaign: formatPct(props.campaign?.confusion), benchmark: "—" },
    { label: "NSP", campaign: formatPct(props.campaign?.nsp), benchmark: "—" },
    { label: "Intention", campaign: formatPct(props.campaign?.intention), benchmark: benchmarkValue("intention") },
  ]);
</script>

<template>
  <AccordionItem value="kpi">
    <AccordionTrigger class="text-base font-semibold">KPI</AccordionTrigger>
    <AccordionContent class="space-y-4 px-1 pt-2">
      <div class="flex items-center gap-2">
        <Switch
          :model-value="isManual"
          @update:model-value="toggleManual" />
        <Label class="text-sm">Benchmark manuel</Label>
      </div>

      <FormField
        v-if="!isManual"
        v-slot="{ componentField }"
        name="jcd_benchmark_id">
        <FormItem>
          <FormLabel>Benchmark</FormLabel>
          <FormControl>
            <Select
              v-bind="componentField"
              :options="benchmarkOptions"
              clearable
              placeholder="Choisir un benchmark" />
          </FormControl>
        </FormItem>
      </FormField>

      <div
        v-if="isManual"
        class="grid grid-cols-3 gap-4">
        <FormField
          v-for="override in overrides"
          :key="override.name"
          v-slot="{ componentField }"
          :name="override.name">
          <FormItem>
            <FormLabel>{{ override.label }}</FormLabel>
            <FormControl>
              <InputNumber
                v-bind="componentField"
                :min="0"
                :max="100"
                :placeholder="placeholderFor(override.key)" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <div
        v-if="showResults"
        class="grid grid-cols-3 gap-y-2 text-sm">
        <div class="text-muted-foreground font-medium">KPI</div>
        <div class="text-muted-foreground font-medium">Campagne</div>
        <div class="text-muted-foreground font-medium">Benchmark</div>
        <template
          v-for="row in rows"
          :key="row.label">
          <div>{{ row.label }}</div>
          <div class="font-medium">{{ row.campaign }}</div>
          <div>{{ row.benchmark }}</div>
        </template>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
